import { createContext, ReactNode, useContext, useEffect, useCallback, useState } from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import { User } from '@shared/schema';
import { queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import {getToken, saveToken, removeToken} from '@/lib/token-storage';
import {
  initializeAuthState,
  isAuthStateReady,
  hasAuthToken,
  subscribeToAuthState,
  updateAuthStateOnLogout
} from '@/lib/auth-state-manager';

interface AuthState {
  isAuthenticated: boolean;
  user: any;
  loading: boolean;
  initializing: boolean; // Nuovo: indica se stiamo ancora inizializzando l'app
  needsRegistration: boolean;
  needsUsername: boolean;
  needsApplication: boolean; // NEW: true when user needs to submit application (not in approved group)
  telegramUser: any;
  hydrationComplete: boolean; // NEW: true when token AND user hydration has settled (success or failure)
  onboardingComplete: boolean; // NEW: true when user has completed onboarding (profile + photos)
  telegramCheckComplete: boolean; // NEW: true when Telegram user check is definitively complete
  // GUEST MODE
  isGuest: boolean; // NEW: true when user is in guest mode (non-registered visitor)
  guestVisitCount?: number; // Number of visits so far (tracking only)
  // PARTNER INVITATION
  pendingPartnerInvitation?: {
    invitedBy: string;
    invitedByUserId: number;
  };
  // BAN STATE
  isBanned?: boolean;
  banReason?: string;
  banType?: string; // 'banned' | 'rejected' | 'permanently_rejected'
  canReapplyAt?: string; // ISO date string for rejected users
  rejectionCount?: number;
}

type AuthContextType = {
  user: User | null;
  setUser: (user: User | null) => void; // NEW: function to update user in auth state
  isLoading: boolean;
  error: Error | null;
  loginMutation: { mutate: (credentials: LoginData) => Promise<void>; isLoading: boolean; isError: boolean; error: any };
  logoutMutation: { mutateAsync: () => Promise<void>; isLoading: boolean; isError: boolean; error: any };
  registerMutation: { mutate: (userData: any) => Promise<void>; isLoading: boolean; isError: boolean; error: any };
  logout: () => Promise<boolean>;
  token: string | null;
  authState: AuthState;
  hydrationComplete: boolean; // NEW: expose hydration complete flag
  onboardingComplete: boolean; // NEW: expose onboarding complete flag
  setOnboardingComplete: () => void; // NEW: helper to mark onboarding as complete
  // GUEST MODE
  guestLogin: () => Promise<{ success: boolean; blocked?: boolean; message?: string }>;
  isGuest: boolean;
  // PARTNER INVITATION
  confirmPartnerInvitation: () => Promise<{ success: boolean; message?: string }>;
  rejectPartnerInvitation: () => Promise<{ success: boolean; message?: string }>;
};

type LoginData = {
  username: string;
  password: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [authStateReady, setAuthStateReady] = useState(false);
  const [registrationCheckAttempts, setRegistrationCheckAttempts] = useState(0); // ⭐ Contatore tentativi

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    initializing: true, // Inizializzazione in corso
    needsRegistration: false,
    needsUsername: false,
    needsApplication: false, // NEW: user needs to submit application
    telegramUser: null,
    hydrationComplete: false, // Initially false, set to true once token/user hydration settles
    onboardingComplete: false, // Initially false, set to true when user completes photo upload onboarding
    telegramCheckComplete: false, // Initially false, set to true when Telegram check definitively completes
    isGuest: false, // Initially false, set to true when user logs in as guest
    guestVisitCount: 0,
    pendingPartnerInvitation: undefined // Initially undefined, set when partner invitation detected
  });

  // Helper to compute onboardingComplete from user data
  // User has completed onboarding if they have at least one photo uploaded
  const computeOnboardingComplete = useCallback((user: any): boolean => {
    if (!user) return false;
    const hasPhotos = Array.isArray(user.photos) && user.photos.length > 0;
    console.log('[AUTH] computeOnboardingComplete:', { userId: user.id, photos: user.photos, hasPhotos });
    return hasPhotos;
  }, []);

  // ⚠️ IMPORTANT: commitAuthState() must be used for ALL TERMINAL auth state updates
  // Terminal states are definitive auth outcomes that complete the hydration process:
  // - Login success/failure, registration success/failure, logout success/error
  // - Token validation results (valid/invalid/expired)
  // - needsUsername/needsRegistration determinations
  // This ensures hydrationComplete=true, allowing components (e.g., PhotoManager2) to proceed
  // without race conditions. Use plain setAuthState() only for TRANSITIONAL states (loading, in-progress).
  // 
  // ONBOARDING LOGIC:
  // - For authenticated users: auto-compute onboardingComplete from user.photos.length > 0 (unless explicitly provided)
  // - For new registrations: explicitly set onboardingComplete=false (needs photo upload)
  // - For logout/error: reset onboardingComplete=false
  // - This ensures onboardingComplete is always correctly rehydrated after app reload
  const commitAuthState = useCallback((nextState: Omit<AuthState, 'hydrationComplete' | 'onboardingComplete' | 'telegramCheckComplete'> & { onboardingComplete?: boolean }) => {
    console.log('[AUTH] Committing auth state with hydration complete:', nextState);
    const { onboardingComplete, ...restState } = nextState;

    // Auto-compute onboardingComplete for authenticated users (unless explicitly provided)
    let finalOnboardingComplete: boolean;
    if (onboardingComplete !== undefined) {
      // Explicitly provided (e.g., registration sets false, setOnboardingComplete sets true)
      finalOnboardingComplete = onboardingComplete;
      console.log('[AUTH] Using explicit onboardingComplete:', finalOnboardingComplete);
    } else if (restState.isAuthenticated && restState.user) {
      // Authenticated user without explicit value - compute from photos
      finalOnboardingComplete = computeOnboardingComplete(restState.user);
      console.log('[AUTH] Auto-computed onboardingComplete:', finalOnboardingComplete);
    } else {
      // Not authenticated - default to false
      finalOnboardingComplete = false;
    }

    setAuthState({
      ...restState,
      hydrationComplete: true,
      onboardingComplete: finalOnboardingComplete,
      telegramCheckComplete: true // Terminal state = Telegram check is definitively complete
    });
  }, [computeOnboardingComplete]);

  // Helper: reset hydration during auth transitions (logout, re-login, etc)
  const resetHydration = useCallback(() => {
    console.log('[AUTH] Resetting hydration state');
    setAuthState(prev => ({
      ...prev,
      hydrationComplete: false,
      initializing: true,
      onboardingComplete: false, // Also reset onboarding on auth transitions
      telegramCheckComplete: false // Also reset telegram check on auth transitions
    }));
  }, []);

  // Helper: mark onboarding as complete (called after photo upload)
  const setOnboardingComplete = useCallback(() => {
    console.log('[AUTH] Marking onboarding as complete');
    setAuthState(prev => ({
      ...prev,
      onboardingComplete: true
    }));
    // Clear just_registered flag when onboarding completes
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('just_registered');
      console.log('[AUTH] Cleared just_registered flag');
    }
  }, []);

  // Function to update user in auth state (used by SimpleCitySelector, etc.)
  const setUser = useCallback((user: User | null) => {
    console.log('[AUTH] setUser called with:', user?.city, user?.latitude, user?.longitude);
    setAuthState(prev => ({
      ...prev,
      user: user
    }));
  }, []);

  // ⭐ RESET contatore tentativi quando autenticazione ha successo
  useEffect(() => {
    if (authState.isAuthenticated || authState.hydrationComplete) {
      console.log('[AUTH] ✅ Autenticazione riuscita o hydration completa, reset contatore tentativi');
      setRegistrationCheckAttempts(0);
    }
  }, [authState.isAuthenticated, authState.hydrationComplete]);

  // ⚡ MONITOR just_registered FLAG - Imposta initializing: false quando viene rimosso
  useEffect(() => {
    const checkFlag = () => {
      const hasFlag = sessionStorage.getItem('just_registered') === 'true';

      // Se il flag è stato rimosso E initializing è ancora true, aggiorna lo stato
      if (!hasFlag && authState.initializing && authState.isAuthenticated) {
        console.log('[AUTH] 🔄 Flag just_registered rimosso, impostando initializing: false');
        setAuthState(prevState => ({
          ...prevState,
          initializing: false,
          loading: false
          // hydrationComplete preserved by spread
        }));
      }
    };

    // Controlla ogni 500ms
    const interval = setInterval(checkFlag, 500);

    return () => clearInterval(interval);
  }, [authState.initializing, authState.isAuthenticated]);

  // Inizializza lo stato di autenticazione
  useEffect(() => {
    console.log('[AUTH] Inizializzazione provider...');
    // Assicurati che initializeAuthState sia chiamato una sola volta
    const init = async () => {
      await initializeAuthState();

      // FORZA invalidazione cache all'avvio per assicurarsi che /api/auth/me venga chiamato
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      console.log('[AUTH] ✅ Cache /api/auth/me invalidata - forzerà refetch');

      setAuthStateReady(true);
      // NOTA: NON impostare initializing: false qui!
      // checkRegistrationStatus() lo farà quando avrà finito il controllo
      console.log('[AUTH] Provider inizializzato e stato pronto.');
    };
    init();

    // Sottoscrivi ai cambiamenti di stato
    const unsubscribe = subscribeToAuthState((result: any) => {
      console.log('[AUTH] Cambiamento stato autenticazione rilevato');

      // Aggiorna lo stato locale di authState - TERMINAL branches use commitAuthState
      if (result.needsUsername) {
        console.log('[AUTH] ⚠️ Utente senza username Telegram (subscriber)');
        // ✅ FIX: Use setAuthState with explicit hydrationComplete=true to show username screen immediately
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: true,
          needsApplication: false,
          telegramUser: result.telegramUser,
          hydrationComplete: true, // ✅ FIX: Show username required screen immediately
          onboardingComplete: false,
          telegramCheckComplete: true, // ✅ FIX: Check complete
          isGuest: false
        });
      } else if (result.needsRegistration) {
        console.log('[AUTH] 📝 Utente necessita registrazione (subscriber)');
        // ⭐ ANTI-FLASH DEFINITIVO: IGNORA SEMPRE il subscriber per needsRegistration!
        // Il subscriber viene chiamato PRIMA che checkRegistrationStatus() completi
        // Lasciamo che checkRegistrationStatus() sia l'UNICO responsabile di settare needsRegistration
        console.log('[AUTH] ⚠️ Subscriber: IGNORANDO needsRegistration - lasciamo che checkRegistrationStatus gestisca');
        // NON modificare lo stato - mantieni quello esistente
      } else if (result.isRegistered && result.user) {
        console.log('[AUTH] ✅ Utente già registrato (subscriber)');
        // ⭐ CRITICO: Non cambiare lo stato se siamo già in verifica token
        // Questo previene flash del form durante il processo di auth
        setAuthState(prevState => {
          // Se stiamo già verificando il token, mantieni needsRegistration: false
          if (prevState.initializing) {
            console.log('[AUTH] Subscriber: mantengo needsRegistration: false durante verifica');
            return {
              ...prevState,
              user: result.user,
              telegramUser: result.telegramUser
            };
          }

          // Altrimenti aggiorna normalmente
          return {
            isAuthenticated: true,
            user: result.user,
            loading: false,
            initializing: false,
            needsRegistration: false,
            needsUsername: false,
            needsApplication: false,
            telegramUser: result.telegramUser,
            hydrationComplete: true,
            onboardingComplete: computeOnboardingComplete(result.user),
            telegramCheckComplete: true,
            isGuest: false
          };
        });
      } else {
        // ⚠️ ANTI-FLASH: Se riceviamo un evento vuoto durante l'inizializzazione,
        // NON modificare lo stato - lascia che checkRegistrationStatus() gestisca tutto
        setAuthState(prevState => {
          if (prevState.initializing || !prevState.telegramCheckComplete) {
            console.log('[AUTH] ⚠️ Ignorando evento vuoto durante inizializzazione (telegramCheckComplete:', prevState.telegramCheckComplete, ')');
            return prevState; // Non modificare nulla
          }

          // ⭐ FIX: Preserve needsApplication state during empty events
          // If user is in the application flow, don't reset their state
          if (prevState.needsApplication) {
            console.log('[AUTH] ⚠️ Ignorando evento vuoto - utente in flow ApplicationOnboarding');
            return prevState; // Don't disrupt the application flow
          }

          // Solo dopo che l'inizializzazione è completa, procedi con logout/reset
          console.log('[AUTH] Stato utente non definito o logout (post-init)');
          return {
            isAuthenticated: false,
            user: null,
            loading: false,
            initializing: false,
            needsRegistration: false,
            needsUsername: false,
            needsApplication: false,
            telegramUser: null,
            hydrationComplete: true,
            onboardingComplete: false,
            telegramCheckComplete: true,
            isGuest: false
          };
        });
      }

      // Forza il re-render delle query
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Funzione per controllare lo stato di registrazione e aggiornare authState
  const checkRegistrationStatus = useCallback(async () => {
    console.log('[AUTH] Check stato registrazione...');

    // ⭐ ANTI-LOOP: Limita i tentativi a massimo 3
    if (registrationCheckAttempts >= 3) {
      console.error('[AUTH] ❌ Troppi tentativi di check registrazione (>=3), mostro errore all\'utente');

      // Mostra messaggio di errore all'utente
      toast({
        title: 'Errore di connessione',
        description: 'Impossibile verificare lo stato di registrazione. Riprova chiudendo e riaprendo l\'app.',
        variant: 'destructive',
        duration: 10000
      });

      // Imposta stato di errore definitivo - STOP al loop
      commitAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initializing: false,
        needsRegistration: true, // Mostra form registrazione come fallback
        needsUsername: false,
        needsApplication: false,
        telegramUser: null,
        isGuest: false
      });
      return;
    }

    // Incrementa contatore tentativi
    setRegistrationCheckAttempts(prev => prev + 1);
    console.log('[AUTH] Tentativo check registrazione #', registrationCheckAttempts + 1);

    // ⚠️ NON modificare lo stato qui - è già impostato dal chiamante
    // Mantieni initializing: true fino alla fine della verifica

    try {
      // 🌐 WEB BROWSER: Se non siamo in Telegram, salta il check Telegram
      const { isTelegramWebApp } = await import('../lib/telegram-service');
      if (!isTelegramWebApp()) {
        console.log('[AUTH] 🌐 Browser web rilevato, skip check Telegram');

        // Prova a validare un token salvato
        const savedToken = getToken();
        if (savedToken) {
          console.log('[AUTH] 🌐 Token trovato, validazione...');
          try {
            const response = await fetch('/api/auth/me', {
              headers: { 'Authorization': `Bearer ${savedToken}` }
            });
            if (response.ok) {
              const payload = await response.json();
              // /api/auth/me responds with { user: {...} } — unwrap so the auth context
              // stores the user fields at the top level (matches Telegram-login path).
              const userData = payload?.user ?? payload;
              console.log('[AUTH] 🌐 Token valido! Utente:', userData.username || userData.id);
              commitAuthState({
                isAuthenticated: true,
                user: userData,
                loading: false,
                initializing: false,
                needsRegistration: false,
                needsUsername: false,
                needsApplication: false,
                telegramUser: null,
                onboardingComplete: true,
                isGuest: false,
              });
              return;
            }
            console.log('[AUTH] 🌐 Token non valido, rimozione...');
            const { removeToken } = await import('@/lib/token-storage');
            removeToken();
          } catch (err) {
            console.error('[AUTH] 🌐 Errore validazione token:', err);
          }
        }

        // Nessun token o token invalido → mostra login web
        console.log('[AUTH] 🌐 Nessun token valido, mostrando login web');
        commitAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: null,
          onboardingComplete: false,
          isGuest: false,
        });
        return;
      }

      // Prima verifica: controlla se siamo in Telegram e se l'utente ha username
      const { checkTelegramUserRegistration } = await import('../lib/telegram-auth');
      const telegramCheck = await checkTelegramUserRegistration();

      // CASO 1: Utente senza username Telegram (PRE-REGISTRATION STATE)
      // ✅ FIX: Set hydrationComplete=true because we KNOW user needs username - show screen immediately
      if (telegramCheck.needsUsername) {
        console.log('[AUTH] ⚠️ Utente senza username Telegram');
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: true,
          needsApplication: false,
          telegramUser: telegramCheck.telegramUser,
          hydrationComplete: true, // ✅ FIX: Check complete - show username required screen immediately
          onboardingComplete: false, // User hasn't registered yet
          telegramCheckComplete: true, // ✅ FIX: Check complete - user needs username
          isGuest: false
        });
        return;
      }

      // CASO BANNED/REJECTED: Utente bannato o rifiutato
      if (telegramCheck.isBanned) {
        console.log('[AUTH] 🚫 Utente BANNATO/RIFIUTATO - tipo:', telegramCheck.banType);
        commitAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: telegramCheck.telegramUser,
          onboardingComplete: false,
          isGuest: false,
          isBanned: true,
          banReason: telegramCheck.banReason || '',
          banType: telegramCheck.banType || 'banned',
          canReapplyAt: telegramCheck.canReapplyAt,
          rejectionCount: telegramCheck.rejectionCount
        });
        return;
      }

      // CASO 2: Utente con username ma non registrato (CONFERMATO DAL DATABASE)
      // ⭐ Auto-login come guest invece di mostrare form registrazione
      if (telegramCheck.needsRegistration) {
        // Check se l'utente vuole registrarsi (cliccato su FloatingRegisterButton)
        const wantToRegister = sessionStorage.getItem('want_to_register') === 'true';

        if (wantToRegister) {
          console.log('[AUTH] 📝 Utente vuole registrarsi - mostrando form registrazione');
          // Rimuovi il flag
          sessionStorage.removeItem('want_to_register');

          // Mostra form registrazione
          setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            initializing: false,
            needsRegistration: true,
            needsUsername: false,
            needsApplication: false,
            telegramUser: telegramCheck.telegramUser,
            hydrationComplete: true,
            onboardingComplete: false,
            telegramCheckComplete: true,
            isGuest: false
          });
          return;
        }

        // ⭐ PRIMA di fare guest login, controlla se è stato invitato come partner
        console.log('[AUTH] 🔍 Controllo se utente è stato invitato come partner...');
        console.log('[AUTH] 🔍 telegramCheck.telegramUser:', telegramCheck.telegramUser);
        const telegramUsername = telegramCheck.telegramUser?.username;
        console.log('[AUTH] 🔍 telegramUsername estratto:', telegramUsername);

        if (telegramUsername) {
          try {
            console.log('[AUTH] 🔍 Chiamando /api/auth/check-partner-invitation/' + telegramUsername);
            const invitationResponse = await fetch(`/api/auth/check-partner-invitation/${telegramUsername}`);
            console.log('[AUTH] 🔍 Risposta status:', invitationResponse.status);
            const invitationData = await invitationResponse.json();
            console.log('[AUTH] 🔍 Risposta dati:', invitationData);

            if (invitationData.hasInvitation) {
              console.log(`[AUTH] 💑 Utente invitato come partner da: ${invitationData.invitedBy}`);
              console.log('[AUTH] ⏸️ Impostando stato pending invitation - modale apparirà...');

              // Imposta stato pending invitation - il modale apparirà
              commitAuthState({
                isAuthenticated: false,
                user: null,
                loading: false,
                initializing: false,
                needsRegistration: false,
                needsUsername: false,
                needsApplication: false,
                telegramUser: telegramCheck.telegramUser,
                isGuest: false,
                pendingPartnerInvitation: {
                  invitedBy: invitationData.invitedBy,
                  invitedByUserId: invitationData.invitedByUserId
                }
              });

              return; // Il modale apparirà e l'utente sceglierà conferma/rifiuta
            }

            console.log('[AUTH] ✅ Nessun invito partner - procedo con guest login');
          } catch (invitationError) {
            console.error('[AUTH] ⚠️ Errore controllo/conferma partner:', invitationError);
            console.error('[AUTH] ⚠️ Errore stack:', invitationError instanceof Error ? invitationError.stack : 'N/A');
            // Procedi comunque con guest login in caso di errore
          }
        } else {
          console.log('[AUTH] ⚠️ telegramUsername NON presente, skip controllo partner');
        }

        console.log('[AUTH] 📝 Utente Telegram non registrato - auto-login guest...');

        // ⭐ ANTI-FLASH: Mantieni initializing: true durante guest login
        // Questo previene che il router mostri il form di registrazione durante il processo
        setAuthState(prevState => ({
          ...prevState,
          loading: true,
          initializing: true,
          needsRegistration: false, // IMPORTANTE: Previeni flash del form
          isGuest: false // Ancora non confermato
        }));

        try {
          // Esegui guest login
          console.log('[AUTH] 🎭 Chiamata a /api/auth/guest-login...');
          const response = await fetch('/api/auth/guest-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ telegramUser: telegramCheck.telegramUser })
          });

          const data = await response.json();
          console.log('[AUTH] 🎭 Risposta guest-login:', { ok: response.ok, data });

          if (response.ok && data.token) {
            console.log('[AUTH] ✅ Guest login automatico riuscito - salvando token...');

            // Salva guest token
            saveToken(data.token);

            // Imposta stato guest CON hydrationComplete e telegramCheckComplete
            console.log('[AUTH] 🎭 Impostando stato guest...');
            commitAuthState({
              isAuthenticated: false,
              user: null,
              loading: false,
              initializing: false,
              needsRegistration: false,
              needsUsername: false,
              needsApplication: false,
              telegramUser: telegramCheck.telegramUser,
              isGuest: true,
              guestVisitCount: data.visitCount
            });

            console.log('[AUTH] ✅ Stato guest impostato correttamente');
            return;
          } else {
            console.error('[AUTH] ❌ Guest login fallito - risposta non ok o token mancante');
            throw new Error(data.error || 'Guest login fallito');
          }
        } catch (error: any) {
          console.error('[AUTH] ❌ Errore guest login automatico:', error);
          console.error('[AUTH] ❌ Stack:', error.stack);
          // Fallback: mostra form registrazione
          console.log('[AUTH] 📝 Fallback: mostrando form registrazione');
          // ⭐ ANTI-FLASH: Usa commitAuthState per garantire transizione pulita
          commitAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            initializing: false,
            needsRegistration: true,
            needsUsername: false,
            needsApplication: false,
            telegramUser: telegramCheck.telegramUser,
            isGuest: false,
            onboardingComplete: false
          });
          return;
        }
      }

      // CASO 2.5: Utente non in gruppo approvato - deve compilare application (CONFERMATO DAL DATABASE)
      // ⭐ hydrationComplete=true perché il DB ha CONFERMATO che l'utente deve fare application
      if (telegramCheck.needsApplication) {
        console.log('[AUTH] 📝 Utente deve compilare ApplicationOnboarding (confermato dal DB)');
        console.log('[AUTH] 📋 Pending status:', (telegramCheck as any).pendingStatus);

        // ⭐ FIX: Remove any stale guest/old token to prevent /api/auth/me query from firing
        // and resetting needsApplication via 401 handler race condition
        const staleToken = getToken();
        if (staleToken) {
          console.log('[AUTH] 🧹 Rimozione token stale per prevenire race condition con /api/auth/me');
          removeToken();
        }

        // ⭐ FIX: Save application flow state to sessionStorage for instant recovery on reload
        sessionStorage.setItem('in_application_flow', JSON.stringify({
          needsApplication: true,
          telegramUser: telegramCheck.telegramUser
        }));

        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: true,
          telegramUser: telegramCheck.telegramUser,
          hydrationComplete: true, // ⭐ TRUE: DB ha confermato che utente deve fare application → mostra form
          onboardingComplete: false, // User hasn't registered yet
          telegramCheckComplete: true, // Check complete - user needs application
          isGuest: false
        });
        return;
      }

      // CASO 3: Utente già registrato
      if (telegramCheck.isRegistered) {
        // IMPORTANTE: Usa il telegramId dal dbUser, non dall'oggetto Telegram raw
        const telegramId = telegramCheck.dbUser?.telegramId || telegramCheck.telegramUser?.id?.toString();
        const token = getToken(telegramId);

        // Se ha un token valido, verifica con il server
        if (token) {
          const headers: HeadersInit = {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store'
          };

          const res = await fetch('/api/auth/me', { headers });

          if (res.ok) {
            const data = await res.json();

            // ⚠️ VERIFICA CRITICA: Controlla che il Telegram ID dell'utente autenticato corrisponda
            // al Telegram ID corrente. Questo previene che utenti diversi usino il token di un altro
            const currentTelegramId = telegramId;
            const authenticatedTelegramId = data?.telegramId?.toString();

            // ⭐ QUICK FIX: Se il token è valido per QUALSIASI utente, aggiorna subito lo stato
            // Questo previene il flash del form anche se c'è un mismatch (che verrà risolto dopo)
            setAuthState(prevState => ({
              ...prevState,
              loading: true,
              initializing: true,
              needsRegistration: false, // ⭐ Token valido = NO registrazione
              isAuthenticated: false // Temporaneo
            }));

            if (currentTelegramId && authenticatedTelegramId && currentTelegramId !== authenticatedTelegramId) {
              console.log(`[AUTH] ❌ TOKEN MISMATCH! Telegram ID corrente: ${currentTelegramId}, Token appartiene a: ${authenticatedTelegramId}`);
              console.log(`[AUTH] L'utente ${currentTelegramId} sta usando il token di ${authenticatedTelegramId} - forzando logout e re-login`);

              // Cancella il token locale
              removeToken();
              localStorage.removeItem('user_data');
              sessionStorage.clear();

              // Forza il re-login automatico con le credenziali corrette
              console.log(`[AUTH] Tentativo di login automatico per Telegram ID: ${currentTelegramId}...`);
              const { loginWithTelegram } = await import('../lib/telegram-auth');
              const loginResult = await loginWithTelegram();

              if (loginResult.success && loginResult.token) {
                console.log(`[AUTH] ✅ Re-login automatico riuscito per ${currentTelegramId}`);
                // Il token è già stato salvato da loginWithTelegram
                // Aggiorna lo stato con il nuovo utente (TERMINAL)
                commitAuthState({
                  isAuthenticated: true,
                  user: loginResult.user,
                  loading: false,
                  initializing: false,
                  needsRegistration: false,
                  needsUsername: false,
                  needsApplication: false,
                  telegramUser: telegramCheck.telegramUser,
                  isGuest: false
                });
                return;
              } else if (loginResult.needsRegistration) {
                console.log(`[AUTH] L'utente ${currentTelegramId} necessita registrazione`);
                setAuthState({
                  isAuthenticated: false,
                  user: null,
                  loading: false,
                  initializing: false,
                  needsRegistration: true,
                  needsUsername: false,
                  needsApplication: false,
                  telegramUser: telegramCheck.telegramUser,
                  hydrationComplete: false, // Explicit false - wait for registration
                  onboardingComplete: false, // User hasn't registered yet
                  telegramCheckComplete: true, // Check complete - user needs registration
                  isGuest: false
                });
                return;
              }
            }

            commitAuthState({
              isAuthenticated: true,
              user: data.user,
              loading: false,
              initializing: false,
              needsRegistration: false,
              needsUsername: false,
              needsApplication: false,
              telegramUser: telegramCheck.telegramUser,
              isGuest: false
            });
            return;
          }
        }

        // ⚠️ CRITICAL: Se l'utente ha appena completato la registrazione, SALTA TUTTO
        // perché il token è già stato salvato e lo stato è già corretto
        const justRegistered = sessionStorage.getItem('just_registered') === 'true';
        if (justRegistered) {
          console.log('[AUTH] 🎉 Flag just_registered rilevato - SKIP auto-login completamente');
          console.log('[AUTH] ➡️ Utente appena registrato, stato già impostato da registerMutation');

          // ✅ FIX: Imposta initializing: false per permettere alla UI di procedere
          setAuthState(prevState => ({
            ...prevState,
            loading: false,
            initializing: false // ⭐ CRITICO: permette al PhotoManager2Onboarding di apparire
            // hydrationComplete preserved by spread
          }));

          // Il RouterWithAuth mostrerà PhotoManager2Onboarding grazie al flag just_registered
          return; // ESCI IMMEDIATAMENTE
        }

        // Se l'utente è registrato MA non ha un token valido, fa il login automatico
        console.log('[AUTH] 🔄 Utente registrato senza token valido, tentativo di login automatico...');
        console.log('[AUTH] 🔍 Utente approvato da pending? Verifico telegramCheck.dbUser:', !!telegramCheck.dbUser);

        // ✅ FIX: MANTIENI initializing: true E needsRegistration: FALSE durante il login automatico
        // ⚡ CRITICAL FIX: Se l'utente ha dbUser (appena approvato), salta il flash completamente
        if (telegramCheck.dbUser) {
          console.log('[AUTH] ✅ Utente appena approvato trovato in dbUser, skip flash form');
          setAuthState(prevState => ({
            ...prevState,
            loading: true,
            initializing: true,
            needsRegistration: false, // ⭐ MANTIENI false per utenti appena approvati
            isAuthenticated: false
          }));
        } else {
          console.log('[AUTH] 🔄 Mantengo stato initializing: true e needsRegistration: false durante auto-login...');
          setAuthState(prevState => ({
            ...prevState,
            loading: true,
            initializing: true, // ⭐ Mantieni il loader
            needsRegistration: false, // ⭐ CRITICO: Previeni flash del form
            isAuthenticated: false // Temporaneo durante auto-login
            // hydrationComplete preserved by spread
          }));
        }


        const { loginWithTelegram } = await import('../lib/telegram-auth');
        const loginResult = await loginWithTelegram();

        if (loginResult.success && loginResult.token) {
          console.log('[AUTH] ✅ Login automatico riuscito!');
          commitAuthState({
            isAuthenticated: true,
            user: loginResult.user,
            loading: false,
            initializing: false,
            needsRegistration: false,
            needsUsername: false,
            needsApplication: false,
            telegramUser: telegramCheck.telegramUser,
            isGuest: false
          });
          return;
        } else if (loginResult.needsApplication) {
          console.log('[AUTH] 📝 Utente necessita application (non in gruppo approvato)');
          console.log('[AUTH] 📝 telegramUser:', loginResult.telegramUser);
          console.log('[AUTH] 📝 Impostando authState con needsApplication: true');

          // ⭐ FIX: Save application flow state to sessionStorage
          sessionStorage.setItem('in_application_flow', JSON.stringify({
            needsApplication: true,
            telegramUser: loginResult.telegramUser || null
          }));

          setAuthState({
            isAuthenticated: false,
            user: null,
            needsRegistration: false,
            needsApplication: true,
            needsUsername: false,
            telegramUser: loginResult.telegramUser || null,
            initializing: false,
            loading: false,
            hydrationComplete: true, // ⭐ FIX: Must be true so RouterWithAuth shows ExternalUserFlow instead of loading screen
            onboardingComplete: false,
            telegramCheckComplete: true, // Check complete - user needs application
            isGuest: false
          });
          return;
        } else {
          // ⚠️ FIX: Se l'auto-login fallisce MA l'utente è GIÀ registrato,
          // forziamo una chiamata diretta al server per generare un nuovo token valido
          console.log('[AUTH] ❌ Login automatico fallito');
          console.log('[AUTH] 🔄 Forzando generazione nuovo token dal server...');

          // ⭐ CRITICO: Mantieni initializing: true per evitare flash del form
          console.log('[AUTH] 🔒 Mantengo initializing: true durante generazione token...');

          try {
            // Chiamata diretta al server per generare un nuovo token
            const response = await fetch('/api/auth/telegram-login-direct', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                telegramId: String(telegramCheck.telegramUser.id),
                username: telegramCheck.telegramUser.username,
                firstName: telegramCheck.telegramUser.first_name,
                lastName: telegramCheck.telegramUser.last_name,
                telegramUsername: telegramCheck.telegramUser.username
              })
            });

            const tokenData = await response.json();

            if (tokenData.success && tokenData.token) {
              console.log('[AUTH] ✅ Token generato con successo dal server!');
              console.log(`[AUTH] 🔍 Tentativo salvataggio per telegramId: ${String(telegramCheck.telegramUser.id)}`);

              // ⭐ CRITICO: Salva il token con telegramId per isolamento
              const telegramIdForStorage = String(telegramCheck.telegramUser.id);
              const saveResult = saveToken(tokenData.token, telegramIdForStorage);
              console.log('[AUTH] 💾 Token salvato (primo tentativo):', saveResult);

              // ⭐ VERIFICA IMMEDIATA con retry se fallisce
              let verifyToken = getToken(telegramIdForStorage);
              let retryCount = 0;
              const maxRetries = 3;

              while (!verifyToken && retryCount < maxRetries) {
                console.error(`[AUTH] ❌ RETRY ${retryCount + 1}/${maxRetries}: Token non trovato, riprovo salvataggio...`);

                // Forza salvataggio multiplo
                saveToken(tokenData.token, telegramIdForStorage);

                // Salva anche in memoria globale come fallback
                if (typeof window !== 'undefined') {
                  (window as any).__authToken = tokenData.token;
                  console.log('[AUTH] 💾 Token salvato in memoria globale come fallback');
                }

                // Attendi 100ms prima di verificare
                await new Promise(resolve => setTimeout(resolve, 100));
                verifyToken = getToken(telegramIdForStorage);
                retryCount++;
              }

              if (!verifyToken) {
                console.error('[AUTH] ❌❌❌ CRITICO FINALE: Token NON salvato dopo tutti i retry!');
                console.error('[AUTH] 🔍 Debug localStorage:', {
                  available: typeof window !== 'undefined' && !!window.localStorage,
                  tokenKey: `auth_token_${telegramIdForStorage}`,
                  allKeys: typeof window !== 'undefined' && window.localStorage ? Object.keys(window.localStorage) : []
                });
              } else {
                console.log('[AUTH] ✅✅✅ Token VERIFICATO con successo!');
              }

              // Solo DOPO aver verificato il salvataggio, imposta initializing: false (TERMINAL)
              commitAuthState({
                isAuthenticated: true,
                user: tokenData.user,
                loading: false,
                initializing: false, // ⭐ Ora è sicuro impostare false
                needsRegistration: false, // ⭐ Esplicito: utente NON necessita registrazione
                needsUsername: false,
                needsApplication: false,
                telegramUser: telegramCheck.telegramUser,
                isGuest: false
              });

              console.log('[AUTH] ✅ Stato aggiornato, utente autenticato');
              return;
            } else {
              console.error('[AUTH] ❌ Server non ha restituito un token valido');
            }
          } catch (tokenError) {
            console.error('[AUTH] ❌ Errore nella generazione token dal server:', tokenError);
          }

          // Fallback finale: usa i dati cached (ma senza token) (TERMINAL)
          console.log('[AUTH] ℹ️ Fallback: uso dati esistenti da telegramCheck.dbUser');

          commitAuthState({
            isAuthenticated: true,
            user: telegramCheck.dbUser,
            loading: false,
            initializing: false,
            needsRegistration: false, // ⭐ Esplicito: utente GIÀ registrato
            needsUsername: false,
            needsApplication: false,
            telegramUser: telegramCheck.telegramUser,
            isGuest: false
          });
          return;
        }
      }

      // FALLBACK: Stato non determinato (utente non registrato) - DB CHECK COMPLETATO
      // ⭐ hydrationComplete=true perché il check è completato (anche se fallback)
      console.log('[AUTH] Stato non determinato, redirect a registrazione');

      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initializing: false,
        needsRegistration: true,
        needsUsername: false,
        needsApplication: false,
        telegramUser: telegramCheck.telegramUser || null,
        hydrationComplete: true, // ⭐ TRUE: check completato, mostra form registrazione
        onboardingComplete: false, // User hasn't registered yet
        telegramCheckComplete: true, // Check complete - user needs registration
        isGuest: false
      });

    } catch (error) {
      console.error('[AUTH] ERRORE CRITICO durante check registrazione:', error);
      // ⚠️ FIX ANTI-FLASH: NON usare commitAuthState qui perché imposta hydrationComplete=true
      // Usa setAuthState con hydrationComplete=false per evitare il flash del form
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initializing: false,
        needsRegistration: true, // Default sicuro
        needsUsername: false,
        needsApplication: false,
        telegramUser: null,
        hydrationComplete: false, // ⭐ CRITICO: non mostrare form finché non siamo sicuri
        onboardingComplete: false,
        telegramCheckComplete: false, // Retry possibile
        isGuest: false
      });
    }
  }, [commitAuthState, registrationCheckAttempts, toast]); // ⭐ Aggiunte dipendenze

  // Validazione automatica del token all'avvio (una sola volta)
  useEffect(() => {
    let hasValidated = false;

    const validateToken = async () => {
      if (hasValidated) return;
      hasValidated = true;

      const { validateTokenWithServer } = await import('@/lib/token-storage');
      const isValid = await validateTokenWithServer();

      if (!isValid) {
        console.log('[AUTH] Token non valido rilevato, NON forzare needsRegistration (potrebbe essere race condition)');
        // ⚠️ FIX ANTI-FLASH: NON usare commitAuthState con needsRegistration=true qui!
        // Il token potrebbe non essere ancora stato salvato da checkRegistrationStatus
        // Lascia che checkRegistrationStatus determini lo stato corretto
        // Solo invalida la cache per forzare un re-fetch
        queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      }
    };

    if (authStateReady) {
      validateToken();
    }
  }, [authStateReady, commitAuthState]);

  // Effettua il check della registrazione all'avvio se lo stato non è pronto
  useEffect(() => {
    if (!authStateReady) return; // Non fare nulla finché l'inizializzazione non è completa

    const checkAuth = async () => {
      try {
        // ⭐ CRITICO: Mantieni initializing true E needsRegistration FALSE durante TUTTA la verifica
        setAuthState(prevState => ({
          ...prevState,
          loading: true,
          initializing: true,
          needsRegistration: false, // ⭐ CRITICO: Previeni flash del form per utenti con token
          isAuthenticated: false // Temporaneo durante verifica
          // hydrationComplete preserved by spread
        }));

        let token = getToken();

        // ⭐ CRITICAL: Se c'è un flag just_registered, SKIP tutto questo useEffect
        // Lo stato è già stato impostato da registerMutation.onSuccess
        const justRegistered = sessionStorage.getItem('just_registered') === 'true';
        if (justRegistered) {
          console.log('[AUTH] 🎉 Flag just_registered trovato - SKIP checkAuth completamente');
          console.log('[AUTH] ➡️ RouterWithAuth mostrerà PhotoManager2Onboarding');

          // ✅ FIX: Imposta initializing: false per permettere alla UI di procedere
          setAuthState(prevState => ({
            ...prevState,
            loading: false,
            initializing: false // ⭐ CRITICO: permette al PhotoManager2Onboarding di apparire
            // hydrationComplete preserved by spread
          }));

          return; // ESCI IMMEDIATAMENTE, non fare checkRegistrationStatus
        }

        // ⭐ FIX DEFINITIVO: Se l'utente è nel flow di application boarding,
        // salta TUTTO il check auth per evitare che qualsiasi meccanismo (401 handler,
        // dev mode refetch, version check, etc.) possa resettare lo stato e smontare ExternalUserFlow.
        const savedApplicationFlow = sessionStorage.getItem('in_application_flow');
        if (savedApplicationFlow) {
          try {
            const flowData = JSON.parse(savedApplicationFlow);
            if (flowData.needsApplication && flowData.telegramUser) {
              console.log('[AUTH] 📝 Flag in_application_flow trovato - ripristino stato application boarding');
              console.log('[AUTH] 📝 telegramId:', flowData.telegramUser.id);

              // Remove any stale tokens that could trigger /api/auth/me 401 race condition
              const staleToken = getToken();
              if (staleToken) {
                console.log('[AUTH] 🧹 Rimozione token stale durante ripristino application flow');
                removeToken();
              }

              setAuthState({
                isAuthenticated: false,
                user: null,
                loading: false,
                initializing: false,
                needsRegistration: false,
                needsUsername: false,
                needsApplication: true,
                telegramUser: flowData.telegramUser,
                hydrationComplete: true,
                onboardingComplete: false,
                telegramCheckComplete: true,
                isGuest: false
              });

              return; // ESCI - vai direttamente a ExternalUserFlow
            }
          } catch (e) {
            console.warn('[AUTH] Errore parsing in_application_flow, continuo con auth normale:', e);
            sessionStorage.removeItem('in_application_flow');
          }
        }

        // FORCE LOGIN: Controllo parametro force_login per dev/test
        const urlParams = new URLSearchParams(window.location.search);
        const forceLoginId = urlParams.get('force_login');

        if (forceLoginId) {
          console.log(`[AUTH] 🎯 FORCE LOGIN rilevato per user ID: ${forceLoginId}`);
          try {
            // Recupera l'utente dal database
            const userResponse = await fetch(`/api/users/${forceLoginId}`);
            if (userResponse.ok) {
              const userData = await userResponse.json();
              console.log(`[AUTH] ✅ Utente force_login trovato:`, userData.username);

              // Effettua il login diretto con i dati dell'utente
              const loginResponse = await fetch('/api/auth/telegram-login-direct', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  telegramId: userData.telegramId || '999999999',
                  username: userData.username,
                  firstName: userData.username,
                  lastName: '',
                  telegramUsername: userData.telegramUsername || userData.username
                })
              });

              const loginData = await loginResponse.json();

              if (loginData.success && loginData.token) {
                console.log(`[AUTH] ✅ Token generato per force_login user ${userData.username}`);

                // Salva il token con il telegramId corretto
                saveToken(loginData.token, userData.telegramId || '999999999');

                // Rimuovi il parametro dall'URL
                urlParams.delete('force_login');
                const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
                window.history.replaceState({}, '', newUrl);

                // TERMINAL: force_login success
                commitAuthState({
                  isAuthenticated: true,
                  user: loginData.user,
                  loading: false,
                  initializing: false,
                  needsRegistration: false,
                  needsUsername: false,
                  needsApplication: false,
                  telegramUser: {
                    id: userData.telegramId || '999999999',
                    username: userData.telegramUsername || userData.username,
                    first_name: userData.username
                  },
                  isGuest: false
                });
                return;
              }
            }
          } catch (error) {
            console.error('[AUTH] ❌ Errore force_login:', error);
          }
        }


        // PRIORITÀ ASSOLUTA: Check URL per testUser
        const testUser = urlParams.get('test_user');

        // Se c'è un test_user in URL diverso dall'utente corrente, forza logout e nuovo login
        if (testUser && process.env.NODE_ENV === 'development') {
          const currentUserHint = localStorage.getItem('dev_user_hint');

          // Se l'utente richiesto è diverso da quello corrente, forza logout
          if (currentUserHint && currentUserHint !== testUser) {
            console.log('[AUTH] 🔄 Cambio utente rilevato:', currentUserHint, '→', testUser);
            const { removeToken } = await import('../lib/token-storage');
            removeToken();
            token = null;
          }
        }

        // Se non c'è token in modalità sviluppo, prova auto-login
        if (!token && process.env.NODE_ENV === 'development') {
          console.log('[AUTH] Nessun token in dev mode, tentativo auto-login...');
          try {
            const { saveToken } = await import('../lib/token-storage');

            // PRIORITÀ 2: Check se siamo nella finestra di fdmal tramite localStorage hint
            const userHint = localStorage.getItem('dev_user_hint');

            let loginData;
            if (testUser === 'fdmal' || userHint === 'fdmal') {
              console.log('[AUTH] 🎯 AUTO-LOGIN COME FDMAL rilevato');
              localStorage.setItem('dev_user_hint', 'fdmal');
              loginData = {
                telegramId: '184929073',
                username: 'fdmal',
                firstName: 'Fdmal',
                lastName: '',
                telegramUsername: 'fdmal'
              };
            } else if (testUser === 'lesfleursdumal' || userHint === 'lesfleursdumal') {
              console.log('[AUTH] 🌸 AUTO-LOGIN COME LESFLEURSDUMAL (257) rilevato');
              localStorage.setItem('dev_user_hint', 'lesfleursdumal');
              loginData = {
                telegramId: '257',
                username: 'lesfleursdumal',
                firstName: 'Lesfleursdumal',
                lastName: '',
                telegramUsername: 'lesfleursdumal'
              };
            } else if (testUser === 'admin' || userHint === 'admin' || testUser === 'sviluppo' || userHint === 'sviluppo' || testUser === '477') {
              console.log('[AUTH] 👑 AUTO-LOGIN COME SVILUPPO (477) rilevato');
              localStorage.setItem('dev_user_hint', 'sviluppo');
              loginData = {
                telegramId: '999999999',
                username: 'sviluppo',
                firstName: 'Sviluppo',
                lastName: '',
                telegramUsername: 'sviluppo'
              };
            } else {
              // Default: sviluppo (477)
              console.log('[AUTH] 👑 Auto-login default come sviluppo (477)...');
              localStorage.setItem('dev_user_hint', 'sviluppo');
              loginData = {
                telegramId: '999999999',
                username: 'sviluppo',
                firstName: 'Sviluppo',
                lastName: '',
                telegramUsername: 'sviluppo'
              };
            }

            console.log('[AUTH] 📡 Invio richiesta login con dati:', {
              telegramId: loginData.telegramId,
              username: loginData.username
            });

            const response = await fetch('/api/auth/telegram-login-direct', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(loginData)
            });

            const data = await response.json();
            console.log('[AUTH] 📨 Risposta login:', { success: data.success, hasToken: !!data.token, hasUser: !!data.user });

            if (data.success && data.token) {
              console.log('[AUTH] ✅ Auto-login riuscito, salvando token...');
              saveToken(data.token);
              token = data.token;
            } else {
              console.error('[AUTH] ❌ Auto-login fallito:', data.message);
              // Auto-login fallito, ma procedi con checkRegistrationStatus
            }
          } catch (loginError) {
            console.error('[AUTH] 💥 Auto-login errore critico:', loginError);
            // Auto-login errore, ma procedi con checkRegistrationStatus
          }
        }

        if (token) {
          console.log('[AUTH] Token trovato, procedo con il check di registrazione.');
          await checkRegistrationStatus();
        } else {
          console.log('[AUTH] Nessun token trovato, procedo con check registrazione.');
          await checkRegistrationStatus();
        }
      } catch (error) {
        console.error('[AUTH] Errore durante il controllo di autenticazione:', error);
        // TERMINAL: checkAuth error
        commitAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: null,
          isGuest: false
        });
      }
    };

    checkAuth();
  }, [authStateReady]); // Rimuovi checkRegistrationStatus dalle dipendenze per evitare loop


  // Verifica che queryClient sia disponibile prima di usare useQuery
  const [authErrorOccurred, setAuthErrorOccurred] = useState(false); // Track if 401 occurred

  let queryResult;
  try {
    // ⭐ FIX: Disable /api/auth/me query when user is in the application flow
    // This prevents a stale guest token from causing a 401 that resets needsApplication
    const queryEnabled = authStateReady && isAuthStateReady() && hasAuthToken() && !authErrorOccurred && !authState.needsApplication;
    console.log('[AUTH] useQuery /api/auth/me - enabled:', queryEnabled, 'authStateReady:', authStateReady, 'hasToken:', hasAuthToken(), 'authErrorOccurred:', authErrorOccurred, 'needsApplication:', authState.needsApplication);

    queryResult = useQuery({
      queryKey: ['/api/auth/me'],
      enabled: queryEnabled,
      refetchOnMount: 'always', // Forza refetch quando il componente monta per evitare dati stale durante navigazione SPA
      // Do NOT auto-refetch on window/tab focus: clicks within the page can flip focus
      // on some browsers (Telegram WebApp included). A stale in-flight response that
      // started before a setUser() (e.g. SimpleCitySelector city change) would land
      // after and overwrite the just-committed user with the previous server snapshot,
      // reverting fields like city. Manual paths invalidate this query when needed.
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on 401 errors - authentication failed
        if (error?.status === 401 || error?.message?.includes('401')) {
          console.log('[AUTH] 401 error detected - disabling retry');
          return false;
        }
        // Retry max 1 time for other errors
        return failureCount < 1;
      },
    queryFn: async () => {
      console.log('[AUTH] ⚡ ESEGUENDO queryFn per /api/auth/me...');

      let tokenValue = null;
      try {
        tokenValue = getToken();
        console.log('[AUTH] Token JWT presente:', !!tokenValue, tokenValue ? `(${tokenValue.substring(0, 10)}...)` : '');
      } catch (error) {
        console.error('[AUTH] Errore nel recupero del token:', error);
      }

      if (!tokenValue) {
        console.log('[AUTH] Nessun token disponibile, utente non autenticato.');
        return null;
      }

      const options: RequestInit = {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${tokenValue}`,
          'Cache-Control': 'no-cache, no-store'
        }
      };

      try {
        console.log('[AUTH] Invio richiesta con token a /api/auth/me...');
        const res = await fetch('/api/auth/me', options);

        if (res.ok) {
          const data = await res.json();
          console.log('[AUTH] Autenticazione riuscita tramite /api/auth/me, utente:', data.user?.username);

          if (tokenValue && data.user) {
            const { saveToken } = await import('../lib/token-storage');
            saveToken(tokenValue); // Assicurati che il token sia valido
          }

          // Aggiorna l'auth state se lo user è presente
          if (data.user) {
            // TRANSITIONAL: partial update during query success
            setAuthState(prevState => ({
              ...prevState,
              user: data.user,
              isAuthenticated: true,
              loading: false,
              needsUsername: false, // Reset per sicurezza
              needsRegistration: false, // Reset per sicurezza
              // hydrationComplete preserved by spread
            }));
          }

          return data.user;
        }

        if (res.status === 401) {
          console.log('[AUTH] Token non valido o scaduto (/api/auth/me), rimozione completa...');

          // Set error flag to disable query immediately
          setAuthErrorOccurred(true);

          removeToken();

          // Pulisci tutto il localStorage per evitare dati corrotti
          try {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('AUTH_TOKEN');
            sessionStorage.removeItem('auth_token');
            sessionStorage.removeItem('just_registered');
            console.log('[AUTH] Storage completamente pulito');
          } catch (error) {
            console.warn('[AUTH] Errore pulizia storage:', error);
          }

          // ⭐ FIX: Preserve needsApplication state if already set by checkRegistrationStatus()
          // This prevents a race condition where the 401 handler overwrites
          // the correct needsApplication state, causing ExternalUserFlow to unmount
          setAuthState(prevState => {
            if (prevState.needsApplication) {
              console.log('[AUTH] ⚠️ 401 ricevuto ma needsApplication è già true - preservo stato application flow');
              return {
                ...prevState,
                loading: false,
                initializing: false,
              };
            }

            console.log('[AUTH] Token obsoleto rimosso, stato completamente resettato');
            return {
              ...prevState,
              isAuthenticated: false,
              user: null,
              loading: false,
              initializing: false,
              needsRegistration: true,
              needsUsername: false,
              needsApplication: false,
              telegramUser: null,
              hydrationComplete: true,
              telegramCheckComplete: true
            };
          });

          // ⚠️ FIX: NO reload - let checkRegistrationStatus handle re-auth
          // Reload can cause infinite loops
          console.log('[AUTH] Stato resettato - la app richiederà nuova autenticazione');

          // Throw error to stop query execution
          throw new Error('401 Unauthorized');
        }

        console.error('[AUTH] Errore dal server (/api/auth/me):', res.status, res.statusText);
        return null;
      } catch (networkError) {
        console.error('[AUTH] Errore di rete durante autenticazione (/api/auth/me):', networkError);
        return null;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minuti - evita refetch continui che causano il loader
    refetchInterval: false
    });
  } catch (error) {
    console.error('[AUTH] Errore useQuery:', error);
    queryResult = {
      data: null,
      error: null,
      isLoading: false,
      refetch: async () => ({})
    };
  }

  const {
    data: user,
    error,
    isLoading: isQueryLoading,
    refetch
  } = queryResult;

  // 🔄 SYNC: Mantieni authState.user sincronizzato con i dati dalla query React Query
  // Questo risolve il problema per cui gli aggiornamenti alla cache non si riflettono nello stato locale
  useEffect(() => {
    if (user && authState.user?.id === user.id) {
      const hasChanges = 
        authState.user.city !== user.city ||
        authState.user.latitude !== user.latitude ||
        authState.user.longitude !== user.longitude ||
        authState.user.isAdmin !== user.isAdmin;
      
      if (hasChanges) {
        console.log('[AUTH] 🔄 SYNC: updating authState.user with fresh data from React Query cache:', {
          oldCity: authState.user.city,
          newCity: user.city,
          oldIsAdmin: authState.user.isAdmin,
          newIsAdmin: user.isAdmin
        });
        setAuthState(prev => ({
          ...prev,
          user: { ...prev.user, ...user }
        }));
      }
    }
  }, [user, authState.user]);

  // In ambiente Telegram non effettuiamo controlli periodici per evitare ricaricamenti continui
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const checkAuth = async () => {
        try {
          await refetch();
        } catch (error) {
          console.error('Errore durante il controllo di autenticazione:', error);
        }
      };

      console.log('Modalità sviluppo: controllo auth periodico attivato');
      const interval = setInterval(checkAuth, 30000);
      return () => clearInterval(interval);
    }

    console.log('Controllo auth periodico disabilitato per evitare ricaricamenti continui');
    return undefined;
  }, [refetch]);

  const loginMutation = {
    mutate: async (credentials: LoginData) => {
      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentials),
          credentials: 'include',
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Login failed');
        }

        const data = await res.json();

        if (data.token) {
          saveToken(data.token);
          console.log('Token JWT salvato dopo login');
        }

        queryClient.setQueryData(['/api/auth/me'], data.user);

        // TERMINAL: login success
        commitAuthState({
          user: data.user,
          isAuthenticated: true,
          loading: false,
          initializing: false,
          needsUsername: false,
          needsRegistration: false,
          needsApplication: false,
          telegramUser: authState.telegramUser || null,
          isGuest: false
        });

        toast({
          title: 'Login effettuato',
          description: 'Bentornato!',
        });
      } catch (error: any) {
        toast({
          title: 'Errore di accesso',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    },
    isLoading: false,
    isError: false,
    error: null,
  };

  const registerMutation = {
    mutate: async (userData: any) => {
      try {
        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
          credentials: 'include',
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Errore durante la registrazione');
        }

        const data = await res.json();

        console.log('REGISTRAZIONE COMPLETATA CON SUCCESSO:', {
          hasToken: !!data.token,
          hasUser: !!data.user,
          tokenLength: data.token ? data.token.length : 0,
          userId: data.user ? data.user.id : null,
          needsApplication: data.needsApplication
        });

        // ⭐ FIX: Handle needsApplication response from registration
        // When user is NOT in an approved group, server returns needsApplication: true
        // without token or user. Route them to ExternalUserFlow instead.
        if (data.needsApplication) {
          console.log('[REG SUCCESS] 📝 Server richiede application - utente non in gruppo approvato');
          console.log('[REG SUCCESS] 📝 pendingUserId:', data.pendingUserId, 'pendingStatus:', data.pendingStatus);

          // ⭐ FIX: Save application flow state to sessionStorage for instant recovery on reload
          sessionStorage.setItem('in_application_flow', JSON.stringify({
            needsApplication: true,
            telegramUser: authState.telegramUser || null
          }));

          setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            initializing: false,
            needsRegistration: false,
            needsUsername: false,
            needsApplication: true,
            telegramUser: authState.telegramUser || null,
            hydrationComplete: true,
            onboardingComplete: false,
            telegramCheckComplete: true,
            isGuest: false
          });

          toast({
            title: 'Registrazione ricevuta!',
            description: 'Completa la richiesta di ammissione per continuare.',
          });

          console.log('[REG SUCCESS] Stato impostato per ApplicationOnboarding');
          return;
        }

        if (data.token) {
          try {
            removeToken();
            const saveResult = saveToken(data.token);
            console.log('[REG SUCCESS] Token salvato:', saveResult);

            if (typeof window !== 'undefined' && window.localStorage) {
              try {
                window.localStorage.setItem('auth_token', data.token);
                console.log('[REG SUCCESS] Token salvato anche in localStorage direttamente');
              } catch (lsError) {
                console.warn('[REG SUCCESS] Errore salvataggio localStorage:', lsError);
              }
            }

            const savedToken = getToken();
            console.log('[REG SUCCESS] Verifica token salvato:', !!savedToken);

            if (!savedToken) {
              console.error('[REG SUCCESS] CRITICO: Token non salvato, tentativo disperato...');
              if (typeof window !== 'undefined') {
                (window as any).__authToken = data.token;
                console.log('[REG SUCCESS] Token salvato in memoria globale come ultima risorsa');
              }
            }
          } catch (error) {
            console.error('[REG SUCCESS] Errore critico nel salvataggio del token:', error);
          }
        }

        // TERMINAL: register success - set onboardingComplete=false (needs photo upload)
        commitAuthState({
          isAuthenticated: true,
          user: data.user,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: null,
          onboardingComplete: false, // NEW: User needs to upload photos
          isGuest: false
        });

        if (data.user) {
          queryClient.setQueryData(['/api/auth/me'], data.user);
          console.log('[REG SUCCESS] Dati utente salvati in cache:', data.user.username);
        }

        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('just_registered', 'true');
          console.log('[REG SUCCESS] Flag just_registered impostato');
        }

        queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
        queryClient.invalidateQueries({ queryKey: ['/api/users/photos'] });

        toast({
          title: 'Registrazione completata!',
          description: 'Il tuo account è stato creato con successo',
        });

        console.log('[REG SUCCESS] Registrazione completata, PhotoManager2Onboarding sarà mostrato dal router');
      } catch (error: any) {
        toast({
          title: 'Errore di registrazione',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    },
    isLoading: false,
    isError: false,
    error: null,
  };

  const logoutMutation = {
    mutateAsync: async () => {
      // TRANSITION: reset hydration at start of logout
      resetHydration();

      try {
        removeToken();
        console.log('Token JWT rimosso dal localStorage');

        const res = await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Logout failed');
        }

        queryClient.setQueryData(['/api/auth/me'], null);
        // TERMINAL: logout success
        commitAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: null,
          isGuest: false
        });
        toast({
          title: 'Logout effettuato',
          description: 'Arrivederci!',
        });
      } catch (error: any) {
        removeToken();
        queryClient.setQueryData(['/api/auth/me'], null);
        // TERMINAL: logout error (still logged out)
        commitAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initializing: false,
          needsRegistration: false,
          needsUsername: false,
          needsApplication: false,
          telegramUser: null,
          isGuest: false
        });
        toast({
          title: 'Errore durante il logout',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    },
    isLoading: false,
    isError: false,
    error: null,
  };

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
      return true;
    } catch (error) {
      console.error("Logout failed:", error);
      return false;
    }
  }, [logoutMutation]);

  // Guest login function
  const guestLogin = useCallback(async (): Promise<{ success: boolean; blocked?: boolean; message?: string }> => {
    try {
      console.log('[GUEST-LOGIN] Starting guest login...');

      // Get Telegram user data
      const telegramWebApp = (window as any).Telegram?.WebApp;
      const telegramUser = telegramWebApp?.initDataUnsafe?.user;

      if (!telegramUser) {
        console.error('[GUEST-LOGIN] No Telegram user found');
        toast({
          title: 'Errore',
          description: 'Telegram user not found',
          variant: 'destructive'
        });
        return { success: false, message: 'Telegram user not found' };
      }

      // Call backend guest-login endpoint
      const response = await fetch('/api/auth/guest-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegramUser })
      });

      const data = await response.json();

      if (!response.ok) {
        // Guest blocked - questo non dovrebbe più accadere con accesso illimitato
        if (data.blocked) {
          console.log('[GUEST-LOGIN] Guest blocked');
          commitAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            initializing: false,
            needsRegistration: true, // Force registration
            needsUsername: false,
            needsApplication: false,
            telegramUser: telegramUser,
            isGuest: false,
            guestVisitCount: data.visitCount
          });

          toast({
            title: 'Errore',
            description: data.error || 'Errore durante il guest login',
            variant: 'destructive'
          });

          return { success: false, blocked: true, message: data.error };
        }

        throw new Error(data.error || 'Guest login failed');
      }

      // Guest login successful
      console.log('[GUEST-LOGIN] Success:', data);

      // Save guest token
      saveToken(data.token);

      // Update auth state for guest
      commitAuthState({
        isAuthenticated: false, // Guest is not "authenticated" (no user account)
        user: null,
        loading: false,
        initializing: false,
        needsRegistration: false,
        needsUsername: false,
        needsApplication: false,
        telegramUser: telegramUser,
        isGuest: true,
        guestVisitCount: data.visitCount
      });

      toast({
        title: 'Modalità ospite',
        description: data.message || 'Accesso come ospite attivato',
      });

      return { success: true, message: data.message };
    } catch (error: any) {
      console.error('[GUEST-LOGIN] Error:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Errore durante il guest login',
        variant: 'destructive'
      });
      return { success: false, message: error.message };
    }
  }, [commitAuthState, toast]);

  // Partner invitation confirmation
  const confirmPartnerInvitation = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    try {
      console.log('[CONFIRM-PARTNER] Confermando invito partner...');

      if (!authState.telegramUser) {
        throw new Error('Dati Telegram mancanti');
      }

      const response = await fetch('/api/auth/partner-confirm-and-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegramUser: authState.telegramUser })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Conferma fallita');
      }

      console.log('[CONFIRM-PARTNER] ✅ Partner confermato');

      // Salva token
      saveToken(data.token);

      // Imposta stato autenticato con l'account condiviso
      commitAuthState({
        isAuthenticated: true,
        user: data.user,
        loading: false,
        initializing: false,
        needsRegistration: false,
        needsUsername: false,
        needsApplication: false,
        telegramUser: authState.telegramUser,
        isGuest: false,
        pendingPartnerInvitation: undefined // Clear invitation
      });

      toast({
        title: 'Partner confermato!',
        description: data.message
      });

      return { success: true, message: data.message };
    } catch (error: any) {
      console.error('[CONFIRM-PARTNER] Errore:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Errore durante la conferma',
        variant: 'destructive'
      });
      return { success: false, message: error.message };
    }
  }, [authState.telegramUser, commitAuthState, toast]);

  // Partner invitation rejection
  const rejectPartnerInvitation = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    try {
      console.log('[REJECT-PARTNER] Rifiutando invito partner, passando a guest...');

      // Esegui guest login
      const result = await guestLogin();

      if (result.success) {
        // Clear pending invitation (already cleared by guestLogin via commitAuthState)
        console.log('[REJECT-PARTNER] ✅ Passato a guest');
      }

      return result;
    } catch (error: any) {
      console.error('[REJECT-PARTNER] Errore:', error);
      return { success: false, message: error.message };
    }
  }, [guestLogin]);

  const currentToken = getToken();

  return (
    <AuthContext.Provider
      value={{
        user: authState.user, // Usa l'user dallo stato locale
        setUser, // NEW: function to update user
        isLoading: authState.loading || isQueryLoading, // Combina i loading states
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        logout,
        token: currentToken,
        authState: authState, // Esponi l'intero stato di autenticazione
        hydrationComplete: authState.hydrationComplete, // NEW: expose hydration complete flag
        onboardingComplete: authState.onboardingComplete, // NEW: expose onboarding complete flag
        setOnboardingComplete, // NEW: expose helper to mark onboarding complete
        // GUEST MODE
        guestLogin,
        isGuest: authState.isGuest,
        // PARTNER INVITATION
        confirmPartnerInvitation,
        rejectPartnerInvitation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
